/**
 * overseerLabels.ts — m31 P2 (O6): the closed vocabulary of click labels.
 *
 * Every `data-ov="…"` in the app comes from this list, and utils/overseerClicks.ts only trusts a `data-ov` that is in it.
 * A click is reported as `sf:<label>` — a name WE assigned to a control, never its visible text, its value or the user's
 * input. Closed on purpose: a label built from data (a page title, a business name) would be PII in the analytics store, and
 * a free-form label would also fragment every heatmap. The T4 test (OVERSEER-304) scans the swept files and fails on any
 * `data-ov` that is not here, and on any <button>/<a> that has none.
 *
 * Shape: `<surface>.<action>` in lower snake case. Pure data, no imports, import.meta-free (jest loads it). Re-exported
 * from services/overseer.ts, next to the funnel and step vocabulary, so there is one place to import the vocabulary from.
 */

// PreviewSaleBar — the floating bar on /preview/:token (an outreach prospect's first look at a redesign).
const SALEBAR = [
  'salebar.get_started', 'salebar.get_this_website', 'salebar.book_demo', 'salebar.dismiss', 'salebar.show_offer',
  'salebar.device_desktop', 'salebar.device_tablet', 'salebar.device_mobile',
] as const;

// SharePage chrome — /share/:token (a customer's design shown to someone they sent the link to).
const SHARE = ['share.sign_in', 'share.what_is', 'share.visit_home'] as const;

// CheckoutPage — header, then one block per wizard step (package, add-ons, domain, account, payment, confirmation).
const CHECKOUT = [
  'checkout.header_home', 'checkout.header_login',
  'checkout.free_generate', 'checkout.package_card', 'checkout.package_select',
  'checkout.addon_card', 'checkout.addon_schedule_call',
  'checkout.domain_mode_register', 'checkout.domain_mode_own', 'checkout.domain_input', 'checkout.domain_search',
  'checkout.domain_advanced', 'checkout.domain_adv_industry', 'checkout.domain_adv_location',
  'checkout.domain_adv_min_words', 'checkout.domain_adv_max_words', 'checkout.domain_filter_search',
  'checkout.domain_suggestion', 'checkout.domain_own_input',
  'checkout.account_login', 'checkout.tos_checkbox', 'checkout.terms_link', 'checkout.privacy_link',
  'checkout.promo_toggle', 'checkout.promo_input', 'checkout.promo_apply', 'checkout.promo_remove',
  'checkout.continue_publish', 'checkout.design_site', 'checkout.pick_other_domain', 'checkout.go_dashboard',
  'checkout.back', 'checkout.continue', 'checkout.place_order', 'checkout.complete_free',
] as const;

// The "Try free" signup dialog opened from the checkout page.
const TRYFREE = [
  'tryfree.close', 'tryfree.email_suggestion', 'tryfree.tos_checkbox', 'tryfree.terms_link', 'tryfree.privacy_link',
  'tryfree.submit', 'tryfree.login',
] as const;

// PaymentLinkLanding — /pay/:token.
const PAYLINK = ['paylink.header_home', 'paylink.submit', 'paylink.set_password', 'paylink.sign_in'] as const;

// UpgradeModal.
const UPGRADE = [
  'upgrade.close', 'upgrade.cancel', 'upgrade.confirm', 'upgrade.done', 'upgrade.add_payment_method',
  'upgrade.package_option', 'upgrade.addon_option',
] as const;

// Dashboard — every button and link on it.
const DASHBOARD = [
  'dashboard.new_website', 'dashboard.account', 'dashboard.get_help', 'dashboard.logout', 'dashboard.more_menu',
  'dashboard.refresh', 'dashboard.create_website', 'dashboard.deploy_link', 'dashboard.onboarding_task',
  'dashboard.connect_domain', 'dashboard.staging_preview', 'dashboard.open_project', 'dashboard.edit_wordpress',
  'dashboard.upgrade', 'dashboard.cancel_plan', 'dashboard.resume_plan',
] as const;

// ProjectWizard — every button and link. The steps a customer takes through it have names of their own; the many small
// tools of the page editor (images, stock photos, palette, legal pages, WordPress import, consolidation…) are named by family.
const WIZARD = [
  // header, mode, errors
  'wizard.new_project', 'wizard.logout', 'wizard.partner_dashboard', 'wizard.add_store_addon', 'wizard.switch_to_guided', 'wizard.dismiss_error',
  // step 1 — domain
  'wizard.create_project', 'wizard.domain_edit', 'wizard.domain_save', 'wizard.domain_cancel',
  // step 2 — design: look & feel, palette, logo, content
  'wizard.brand_feel', 'wizard.brand_feel_clear', 'wizard.palette_recommend', 'wizard.palette_apply', 'wizard.palette_dismiss',
  'wizard.palette_revert', 'wizard.logo_remove', 'wizard.summarize_content',
  // step 2 — design: pages
  'wizard.generate_all', 'wizard.consolidate_recommend', 'wizard.page_mode_generate', 'wizard.page_mode_redesign', 'wizard.page_mode_upload',
  'wizard.page_section_toggle', 'wizard.page_remove', 'wizard.page_preview_toggle', 'wizard.redesign_generate', 'wizard.source_url_add',
  'wizard.source_url_remove', 'wizard.edit_page', 'wizard.page_undo', 'wizard.page_edit_cancel', 'wizard.page_report', 'wizard.open_editor',
  'wizard.element_clear', 'wizard.element_tag', 'wizard.category_confirm', 'wizard.category_cancel', 'wizard.add_page', 'wizard.add_legal_pages',
  'wizard.legal_source_ai', 'wizard.legal_source_own', 'wizard.legal_generate',
  // step 2 — design: images
  'wizard.images_toggle', 'wizard.image_remove_bg', 'wizard.image_delete', 'wizard.stock_photo_open', 'wizard.stock_photo_search',
  'wizard.stock_photo_pick', 'wizard.stock_photo_credit',
  // step 2 — design: online store
  'wizard.ecommerce_add', 'wizard.ecommerce_remove',
  // consolidation dialog
  'wizard.consolidation_close', 'wizard.consolidation_toggle', 'wizard.consolidation_page', 'wizard.consolidation_cancel', 'wizard.consolidation_apply',
  // step 3 — build and publish
  'wizard.back_to_domain', 'wizard.continue_to_build', 'wizard.choose_plan', 'wizard.target_production', 'wizard.target_staging',
  'wizard.connect_domain', 'wizard.build', 'wizard.local_wp_deploy', 'wizard.retry_deploy', 'wizard.result_link', 'wizard.go_dashboard',
  'wizard.back_from_build', 'wizard.wp_fetch', 'wizard.wp_status', 'wizard.wp_select_all', 'wizard.wp_diff_toggle', 'wizard.wp_import',
  // the guided (conversational) wizard's "Tell us about your business" step — the three optional facts asked when the source is
  // thin (m31 P5 I1, #2429; components/OwnerFactsFields.tsx). They are inputs, so a click on one is "the owner reached this field".
  'wizard.facts_services', 'wizard.facts_area', 'wizard.facts_different',
] as const;

// ManagePage — every button and link.
const MANAGE = [
  'manage.back_dashboard', 'manage.view_preview', 'manage.go_live', 'manage.view_live', 'manage.edit_in_wordpress',
  'manage.pull_from_wp', 'manage.share_design', 'manage.redeploy', 'manage.publish_all', 'manage.add_page',
  'manage.page_edit', 'manage.page_publish', 'manage.page_rename', 'manage.page_delete', 'manage.page_pull_wp',
  'manage.system_page_edit', 'manage.tab', 'manage.choose_plan', 'manage.upgrade', 'manage.cancel_plan',
  'manage.resume_plan', 'manage.build_site', 'manage.check_domain', 'manage.contact_support',
  'manage.dismiss_error', 'manage.retry_pages', 'manage.legal_open', 'manage.legal_close', 'manage.legal_source_ai',
  'manage.legal_source_own', 'manage.legal_generate', 'manage.aside_addon',
] as const;

export const OV_LABELS = [
  ...SALEBAR, ...SHARE, ...CHECKOUT, ...TRYFREE, ...PAYLINK, ...UPGRADE, ...DASHBOARD, ...WIZARD, ...MANAGE,
] as const;

export type OvLabel = (typeof OV_LABELS)[number];

// Built on first use, NOT at module load: a top-level `new Set(OV_LABELS)` is a side effect the bundler must keep, and it would
// drag the whole list into the entry chunk even though nothing at runtime asks for it (see hasOvLabelShape).
let labelSet: ReadonlySet<string> | null = null;

/** True only for a label in the closed vocabulary. Used by the tests (T4 pins every `data-ov` in src to it) — NOT at runtime, see below. */
export function isOvLabel(value: unknown): value is OvLabel {
  if (!labelSet) labelSet = new Set(OV_LABELS);
  return typeof value === 'string' && labelSet.has(value);
}

/** The shape every label has: `<surface>.<action>` in lower snake case. */
export const OV_LABEL_SHAPE = /^[a-z][a-z0-9]*\.[a-z][a-z0-9_]*$/;

/**
 * What the click listener checks at RUNTIME. It reads the shape, not the list, on purpose: this module is imported by the
 * code that loads on every page view, and the list is ~190 strings (about 3 kB gzip) that would travel with it for no
 * gain — a `data-ov` is written in source, never built from data, and the closed list is enforced where it can be, at
 * build time: the T4 test (OVERSEER-304) fails on any `data-ov` in src that is not in OV_LABELS. The shape still keeps out
 * anything that could be text (a space, an `@`, upper case, a missing dot).
 */
export function hasOvLabelShape(value: unknown): value is string {
  return typeof value === 'string' && value.length <= 40 && OV_LABEL_SHAPE.test(value);
}

/**
 * The click selector the same-origin proxy accepts: `sf:` + a label that starts with a letter or digit and continues with
 * letters, digits and `_ . : -`, at most 116 characters. It is the proxy's own `SF_LABEL_RE`
 * (packages/backend/src/services/overseer-scrub.service.ts), copied here because the browser cannot import the backend; a
 * selector outside it is DROPPED server-side as a bad event, so the client refuses to send one. The drift test
 * (OVERSEER-319) compares this source against the backend's, so the two cannot diverge unnoticed.
 *
 * What fits the shape (utils/overseerClicks.ts builds all of these):
 *   checkout.place_order            a data-ov label
 *   id:card-number                  an element id
 *   button  /  div:button           a tag, or tag:role
 *   a:p.dashboard.project._.manage  a link to an internal path ('_' stands for a segment that looks like an id)
 *   a:external  /  a:mailto         a link to elsewhere / to a person
 *   pv:hero:cta  /  pv:s3:form      a click inside a previewed site (utils/overseerPreviewFrame.ts)
 */
export const OV_SELECTOR_RE = /^sf:[A-Za-z0-9][A-Za-z0-9_.:-]{0,115}$/;

export function isOvSelector(value: unknown): value is string {
  return typeof value === 'string' && OV_SELECTOR_RE.test(value);
}
